import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import get from 'lodash/get';
import { handleSubmissionErrors } from '../../../../utils/errors'
import Layout from '../../../../layout';
import modalTitles from '../../../../utils/modal-title-options';
import * as propertyActions from '../../../../actions/properties';
import * as sidebarActions from '../../../../actions/sidebar';
import HotelMeetingForm from '../../../../components/HotelMeetingForm';
import FormNavigator from '../../../../components/FormNavigator';
import * as tiledPromoActions from '../../../../actions/tiledPromos';
import * as imageActions from '../../../../actions/images';
import ctaTitleOptions from '../../../../utils/cta-title-options';

const propTypes = {
  hotel: PropTypes.object.isRequired,
  meeting: PropTypes.object.isRequired,
  images: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  updateTiledPromo: PropTypes.func.isRequired,
  fetchProperty: PropTypes.func.isRequired,
  fetchTiledPromo: PropTypes.func.isRequired,
  updateSidebar: PropTypes.func.isRequired,
  setCurrentImage: PropTypes.func.isRequired
};

const sections = [
  { id: 'title', title: 'Title & Content' },
  { id: 'date', title: 'Date & Time' },
  { id: 'image', title: 'Set an Image' },
  { id: 'upload', title: 'Upload a PDF' },
  { id: 'cta', title: 'CTA (Call to Action)' },
  { id: 'preview', title: 'Preview + Publish' },
];

const MeetingsEdit = ({
  hotel,
  meeting,
  isLoading,
  match: {
    params: { hotelid, id }
  },
  history: { push },
  images,
  updateTiledPromo,
  fetchProperty,
  fetchTiledPromo,
  updateSidebar,
  setCurrentImage
}) => {
  useEffect(() => {
    fetchProperty(hotelid).then(property => {
      updateSidebar({ titles: { Hotel: property.name } });
    });
    fetchTiledPromo(hotelid, id);
    return () => {};
  }, []);

  useEffect(() => {
    get(meeting, 'images', []).forEach(image => {
      setCurrentImage('hotel-promos-image', image);
    });
    return () => {};
  }, [meeting]);

  const handleSubmit = data => {
    /* eslint-disable no-param-reassign */
    let { pdf } = data;
    pdf = pdf && pdf.id ? { assetId: pdf.id } : null;

    const update = {
      ...data,
      pdf,
      startsOn: data.startsOn?.toJSON(),
      endsOn: data.endsOn ? data.endsOn.toJSON() : data.endsOn,
      images,
      modal1Title:
        // eslint-disable-next-line no-nested-ternary
        data.modal1Title === 'None'
          ? null
          : data.modal1Title === 'Other'
          ? data.modal1TitleOther
          : data.modal1Title,
      modal2Title:
        // eslint-disable-next-line no-nested-ternary
        data.modal2Title === 'None'
          ? null
          : data.modal2Title === 'Other'
          ? data.modal2TitleOther
          : data.modal2Title,
      cta: Object.assign(
        data.cta,
        data.cta.type === 'Other' && { type: data.cta.typeInput }
      )
    };
    return updateTiledPromo(hotelid, id, update)
      .then(() => push(`/hotels/${hotelid}/tiled/meetings`))
      .catch(e => {
        handleSubmissionErrors(e)
      });
  };

  return (
    <Layout metaTitle="Edit Meetings Promo">
      {isLoading ? (
        <div>Loading ...</div>
      ) : (
        <div className="w-full max-w-3xl">
          <Link
            to={`/hotels/${hotelid}/tiled/meetings`}
            className="mb-8 inline-block text-sm text-grey-dark hover:text-grey-darkest no-underline"
          >
            &lt; Back to Meetings Promos
          </Link>
          <h1 className="mb-6">Edit Meetings Promo</h1>
          <div className="mb-16">
            Use this page to edit {meeting.title} Meetings Promo on {hotel.name}
            .
          </div>
          <HotelMeetingForm
            id="edit-meetings-form"
            category="Meetings"
            hotel={hotel}
            initialValues={{
              ...meeting,
              // eslint-disable-next-line no-nested-ternary
              modal1Title: !meeting.modal1Title
                ? 'None'
                : modalTitles.findIndex(
                    title => title.id === meeting.modal1Title
                  ) === -1
                ? 'Other'
                : meeting.modal1Title,
              modal1TitleOther:
                modalTitles.findIndex(
                  title => title.id === meeting.modal1Title
                ) === -1
                  ? meeting.modal1Title
                  : undefined,
              // eslint-disable-next-line no-nested-ternary
              modal2Title: !meeting.modal2Title
                ? 'None'
                : modalTitles.findIndex(
                    title => title.id === meeting.modal2Title
                  ) === -1
                ? 'Other'
                : meeting.modal2Title,
              modal2TitleOther:
                modalTitles.findIndex(
                  title => title.id === meeting.modal2Title
                ) === -1
                  ? meeting.modal2Title
                  : undefined,
              showmodal2: !!meeting.modal2Title,
              cta: meeting.cta && {
                ...meeting.cta,
                type:
                  ctaTitleOptions.findIndex(
                    title => title.id === get(meeting, 'cta.type')
                  ) === -1
                    ? 'Other'
                    : get(meeting, 'cta.type'),
                typeInput:
                  ctaTitleOptions.findIndex(
                    title => title.id === get(meeting, 'cta.type')
                  ) === -1
                    ? get(meeting, 'cta.type')
                    : undefined
              },
              startsOn: meeting.startsOn
                ? new Date(meeting.startsOn)
                : meeting.startsOn,
              endsOn: meeting.endsOn ? new Date(meeting.endsOn) : meeting.endsOn
            }}
            onSubmit={handleSubmit}
            enableReinitialize
            keepDirtyOnReinitialize
          />
        </div>
      )}
      <FormNavigator
        id="edit-meetings-navigator"
        sections={sections}
        className="hidden xl:block mt-32 pt-16"
      />
    </Layout>
  );
};

MeetingsEdit.propTypes = propTypes;

export default connect(
  (state, props) => ({
    hotel: state.property.data,
    meeting: state.tiledPromo.data,
    images: get(state, 'images.hotel-promos-image.items', [])
      .filter(item => item.file)
      .map(item => ({ assetId: item.file.id, alt: item.alt })),
    isLoading: state.property.isLoading || state.tiledPromo.isLoading
  }),
  {
    ...propertyActions,
    ...sidebarActions,
    ...tiledPromoActions,
    ...imageActions
  }
)(MeetingsEdit);
